/**
 * FilterPanelAccordion — accordion-wrapped FilterPanel (#704 PR-5 tablet
 * + PR-6 desktop).
 *
 * On the tablet breakpoint (768-1024px) the desktop FilterPanel sidebar
 * is too tall for a comfortable scroll: 18 filter groups in one flat
 * pane. This wrapper reuses PR-3's FilterSheet 4-section accordion
 * grouping (Location & Size / Price & Market / Land Quality / Advanced)
 * to give the user the same orienting chrome as the phone sheet,
 * inside the existing sidebar slot.
 *
 * PR-6 (#704) — used at desktop too: same 4-section accordion replaces
 * the flat FilterPanel sidebar at ≥1025px so the three breakpoints
 * share one filter-grouping mental model. Section open/closed state
 * persists per-section in localStorage under
 * `landai.filter.section.<id>` so the user's preferences survive a
 * page reload.
 *
 * Grouped rendering (#704 follow-up): each section body now renders the
 *   FilterPanel controls that belong to that group via
 *   <FilterPanel group={section.key} /> — so the inputs live INSIDE the
 *   section the header names, not described above and dumped flat below.
 *   The action buttons (Apply / Reset / Export / Save) render once below
 *   the sections via a single <FilterPanel actionsOnly />. Collapsing a
 *   section unmounts only that group's inputs; filter state is lifted to
 *   SearchPage so nothing is lost, and the actionsOnly footer keeps its
 *   own validator alive so Apply still gates on area validation even when
 *   Location & Size is collapsed.
 *
 * Rendered by SearchPage when useBreakpoint() === 'tablet' OR 'desktop'.
 * Phone still uses FilterSheet (the modal). All three branches consume
 * the same FilterPanel underneath.
 */
import React, { useState, useEffect } from 'react';
import FilterPanel from './FilterPanel';

// localStorage key prefix for per-section open/closed persistence
// (PR-6, #704). One key per section so a future section-add doesn't
// invalidate the user's saved state for the other three.
const SECTION_STATE_KEY_PREFIX = 'landai.filter.section.';

function readSectionOpen(key, fallback) {
  try {
    const raw = localStorage.getItem(SECTION_STATE_KEY_PREFIX + key);
    if (raw === '1') return true;
    if (raw === '0') return false;
  } catch { /* SecurityError on private-mode + Safari — fall through */ }
  return fallback;
}

function writeSectionOpen(key, isOpen) {
  try {
    localStorage.setItem(SECTION_STATE_KEY_PREFIX + key, isOpen ? '1' : '0');
  } catch { /* ignore quota / security errors */ }
}

// Same section descriptors as FilterSheet so phone + tablet feel like
// siblings. `defaultOpen` follows the blueprint: first three open,
// Advanced collapsed.
const SECTIONS = [
  {
    key: 'location-size',
    title: 'Location & Size',
    description: 'State, acreage range, free-text search by APN / address / owner / MLS.',
    defaultOpen: true,
  },
  {
    key: 'price-market',
    title: 'Price & Market',
    description: 'List price, $/acre, days on market, recent price changes.',
    defaultOpen: true,
  },
  {
    key: 'land-quality',
    title: 'Land Quality',
    description: 'Listing status, encumbrances (liens / easements / landlocked), motivated sellers, hazard risk, elevation change, bookmarks.',
    defaultOpen: true,
  },
  {
    key: 'advanced',
    title: 'Advanced',
    description: 'Mineral probabilities (Au / Cu / Ag / U / Li / REE), distance to nearest deposit, deposit count nearby.',
    defaultOpen: false,
  },
];

function initialOpenState() {
  // PR-6 (#704): hydrate from localStorage per-section, falling back
  // to the section's blueprint default. We do this once at mount —
  // a SSR-friendly lazy useState initializer — so the first paint
  // already reflects the user's last preference and we avoid a flash
  // of default-open -> persisted-closed.
  const out = {};
  for (const s of SECTIONS) {
    out[s.key] = readSectionOpen(s.key, !!s.defaultOpen);
  }
  return out;
}

function FilterPanelAccordion({ filters, setFilters }) {
  const [openSections, setOpenSections] = useState(() => initialOpenState());

  // PR-6: persist per-section state. Per-key so a future section-add
  // doesn't blow away the user's saved preferences.
  useEffect(() => {
    for (const s of SECTIONS) {
      writeSectionOpen(s.key, !!openSections[s.key]);
    }
  }, [openSections]);

  const toggleSection = (sectionKey) => {
    setOpenSections((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  return (
    <div
      className="filter-panel-accordion"
      data-testid="filter-panel-accordion"
    >
      <div className="filter-panel-accordion-sections">
        {SECTIONS.map((section) => {
          const isOpen = !!openSections[section.key];
          return (
            <div
              key={section.key}
              className={`filter-panel-accordion-section ${isOpen ? 'filter-panel-accordion-section--open' : ''}`}
              data-testid={`filter-panel-accordion-section-${section.key}`}
            >
              <button
                type="button"
                className="filter-panel-accordion-header"
                onClick={() => toggleSection(section.key)}
                aria-expanded={isOpen}
                aria-controls={`filter-panel-accordion-body-${section.key}`}
                data-testid={`filter-panel-accordion-toggle-${section.key}`}
              >
                <span className="filter-panel-accordion-title">{section.title}</span>
                <span className="filter-panel-accordion-chevron" aria-hidden="true">
                  {isOpen ? '▾' : '▸'}
                </span>
              </button>
              {isOpen && (
                <div
                  id={`filter-panel-accordion-body-${section.key}`}
                  className="filter-panel-accordion-body"
                  data-testid={`filter-panel-accordion-body-${section.key}`}
                >
                  <FilterPanel
                    filters={filters}
                    setFilters={setFilters}
                    group={section.key}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Action footer (Apply / Reset / Export / Save) rendered once
          below the sections. Wrapped in a div so a future PR can scope
          sticky / scroll behavior here without editing FilterPanel.js. */}
      <div
        className="filter-panel-accordion-panel"
        data-testid="filter-panel-accordion-panel"
      >
        <FilterPanel filters={filters} setFilters={setFilters} actionsOnly />
      </div>
    </div>
  );
}

export default FilterPanelAccordion;
